import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, BedDouble, CalendarDays, Images, MapPin, Star, Users } from "lucide-react";
import { API, api } from "@/api";
import { pick, useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

/** Pagina pubblica Alloggi: strutture reali del catalogo (Google Places),
 *  foto vere, valutazioni verificate. Nessun prezzo mostrato: non verificato —
 *  il prezzo arriva nel preventivo finale. */
export default function Alloggi() {
    const { lang, t } = useLang();
    const [params, setParams] = useSearchParams();
    const city = params.get("city") || "";
    const [destinations, setDestinations] = useState([]);
    const [stays, setStays] = useState(null);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        api.get("/public/builder/italy/destinations").then((r) => setDestinations(r.data.destinations || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!city) { setStays(null); return; }
        setStays(null);
        setLoadError(false);
        api.get(`/public/stays/${city}`)
            .then((r) => setStays(r.data.stays || []))
            .catch(() => { setStays([]); setLoadError(true); });
    }, [city]);

    const cityName = useMemo(() => {
        const d = destinations.find((x) => x.id === city);
        return d ? pick(d.name, lang) : "";
    }, [destinations, city, lang]);

    const dates = params.get("checkin") && params.get("checkout") ? `${params.get("checkin")} → ${params.get("checkout")}` : null;

    return (
        <div data-testid="stays-page" className="marble-bg min-h-screen pt-32 pb-24">
            <Seo lang={lang} title={`${t.staysPage.title} — TOURVOYAGERS`} description={t.staysPage.sub} />
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <Reveal>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium">{t.staysPage.overline}</p>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-tight">{t.staysPage.title}</h1>
                    <p className="mt-5 max-w-2xl text-base text-stone-300 leading-relaxed">{t.staysPage.sub}</p>
                </Reveal>

                {dates && (
                    <div className="mt-8 flex flex-wrap items-center gap-4 rounded-xl border border-amber-500/25 bg-[#121216] px-6 py-4 text-xs text-stone-300" data-testid="stays-your-search">
                        <span className="inline-flex items-center gap-2"><CalendarDays size={13} className="text-amber-400" />{dates}</span>
                        <span className="inline-flex items-center gap-2"><Users size={13} className="text-amber-400" />{params.get("guests")} {t.builder.prefPax.toLowerCase()} · {params.get("rooms")} {t.staysPage.rooms.toLowerCase()}</span>
                        {params.get("type") && params.get("type") !== "any" && <span className="inline-flex items-center gap-2"><BedDouble size={13} className="text-amber-400" />{t.search.stayTypes[params.get("type")]}</span>}
                    </div>
                )}

                <div className="mt-10 flex flex-wrap gap-2" data-testid="stays-city-tabs">
                    {destinations.map((d) => (
                        <button key={d.id} data-testid={`stays-city-${d.id}`}
                            onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.set("city", d.id); return n; })}
                            className={`rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors ${city === d.id ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/30 text-stone-300 hover:border-amber-400/60"}`}>
                            {pick(d.name, lang)}
                        </button>
                    ))}
                </div>

                <div className="mt-12">
                    {!city && <p className="text-sm text-stone-500" data-testid="stays-choose">{t.staysPage.choose}</p>}
                    {city && stays === null && !loadError && <p className="text-sm text-stone-500">{t.common.loading}</p>}
                    {loadError && (
                        <div className="rounded-xl border border-amber-500/25 bg-[#121216] p-8 text-center" data-testid="stays-error">
                            <p className="text-sm text-stone-300">{t.common.catalogError}</p>
                        </div>
                    )}
                    {stays && stays.length > 0 && (
                        <>
                            <p className="mb-6 text-xs uppercase tracking-[0.2em] text-amber-500/80" data-testid="stays-count">
                                {stays.length} {t.staysPage.resultsIn} {cityName}
                            </p>
                            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {stays.map((s, i) => (
                                    <Reveal key={s.place_id} delay={Math.min(i, 8) * 0.05}>
                                        <div className="gold-card group flex h-full flex-col overflow-hidden rounded-2xl" data-testid={`stay-card-${s.place_id}`}>
                                            <div className="relative aspect-[4/3] overflow-hidden">
                                                <img src={`${API}/public/stays/${city}/${s.place_id}/photo`} alt={s.name} loading="lazy"
                                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                                <div className="absolute inset-0 bg-gradient-to-t from-[#080909]/70 to-transparent" />
                                                {s.photo_count > 1 && (
                                                    <span className="absolute bottom-2.5 right-3 inline-flex items-center gap-1 rounded-full bg-[#0b0b0c]/85 px-2 py-1 text-[10px] text-amber-200">
                                                        <Images size={10} /> {s.photo_count}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-1 flex-col p-5">
                                                <h3 className="font-display text-lg leading-snug text-amber-50">{s.name}</h3>
                                                <p className="mt-1 flex items-center gap-1.5 text-[11px] text-stone-500"><MapPin size={11} className="text-amber-500/70" />{s.address?.split(",").slice(0, 2).join(",")}</p>
                                                {s.rating && (
                                                    <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-stone-400">
                                                        <Star size={11} className="fill-amber-400 text-amber-400" />{s.rating} <span className="text-stone-600">({s.reviews} {t.staysPage.reviews})</span>
                                                    </p>
                                                )}
                                                <p className="mt-3 text-[10px] leading-snug text-stone-500">{t.builder.stayPriceNote}</p>
                                                <Link to={`/itinerario/italia?dest=${city}`} data-testid={`stay-cta-${s.place_id}`}
                                                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-amber-500/50 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-200 transition-colors hover:bg-amber-400 hover:text-black">
                                                    {t.staysPage.cta} <ArrowRight size={12} />
                                                </Link>
                                            </div>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        </>
                    )}
                    {stays && stays.length === 0 && !loadError && <p className="text-sm text-stone-500" data-testid="stays-empty">{t.staysPage.empty}</p>}
                </div>
            </div>
        </div>
    );
}
